import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ViewProvider } from './context/ViewContext';
import Registration from './components/Registration';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import OrderDetails from './components/OrderDetails';
import Sidebar from './components/Sidebar';
import ProductionSummary from './components/ProductionSummary';
import DashboardCharts from './components/DashboardCharts';
import Inventory from './components/Inventory';
import ManageOrders from './components/ManageOrders';
import ViewToggle from './components/ViewToggle';
import RawMaterialRequests from './components/RawMaterialRequests';
// Owner-specific components
import OwnerOrders from './components/OwnerOrders';
import OwnerInventory from './components/OwnerInventory';
import OwnerAlerts from './components/OwnerAlerts';
import CompanyPerformance from './components/CompanyPerformance';
import OwnerTickets from './components/OwnerTickets';
import './App.css';
import { Menu } from 'lucide-react';

// Layout wrapper for authenticated pages with sidebar
const MainLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="main-layout">
      {/* Mobile Header */}
      <div className="mobile-nav-header">
        <div className="mobile-logo">Rotica</div>
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(true)}
        >
          <Menu size={24} />
        </button>
      </div>

      <Sidebar isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Overlay */}
      <div
        className={`sidebar-overlay ${mobileMenuOpen ? 'show' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      <main className="main-content-area">
        <ViewToggle />
        {children}
      </main>
    </div>
  );
};

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const user = localStorage.getItem('user');
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <MainLayout>{children}</MainLayout>;
};

function App() {
  return (
    <ViewProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Registration />} />
            <Route path="/login" element={<Login />} />

            {/* Protected Routes with Sidebar */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/production" element={
              <ProtectedRoute>
                <ProductionSummary />
              </ProtectedRoute>
            } />
            <Route path="/manage-orders" element={
              <ProtectedRoute>
                <ManageOrders />
              </ProtectedRoute>
            } />
            <Route path="/orders" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/order/:id" element={
              <ProtectedRoute>
                <OrderDetails />
              </ProtectedRoute>
            } />
            <Route path="/charts" element={
              <ProtectedRoute>
                <DashboardCharts />
              </ProtectedRoute>
            } />
            <Route path="/inventory" element={
              <ProtectedRoute>
                <Inventory />
              </ProtectedRoute>
            } />

            {/* Owner-specific Routes */}
            <Route path="/owner/orders" element={
              <ProtectedRoute>
                <OwnerOrders />
              </ProtectedRoute>
            } />
            <Route path="/owner/production" element={
              <ProtectedRoute>
                <ProductionSummary isOwner={true} />
              </ProtectedRoute>
            } />
            <Route path="/owner/manage-orders" element={
              <ProtectedRoute>
                <ManageOrders isOwner={true} />
              </ProtectedRoute>
            } />
            <Route path="/owner/inventory" element={
              <ProtectedRoute>
                <OwnerInventory />
              </ProtectedRoute>
            } />
            <Route path="/owner/alerts" element={
              <ProtectedRoute>
                <OwnerAlerts />
              </ProtectedRoute>
            } />
            <Route path="/owner/raw-material-requests" element={
              <ProtectedRoute>
                <RawMaterialRequests />
              </ProtectedRoute>
            } />
            <Route path="/company-performance" element={
              <ProtectedRoute>
                <CompanyPerformance />
              </ProtectedRoute>
            } />
            <Route path="/owner/tickets" element={
              <ProtectedRoute>
                <OwnerTickets />
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </Router>
    </ViewProvider>
  );
}

export default App;
